import { AxiosResponse } from "axios";
import { httpRequest } from "@/lib/axios";
import { useQuery } from "@tanstack/react-query";
import { Stage, StagesResponse, StageFiltersProps } from "@/types";

// Função para buscar estágios
async function fetchStages(
  filters?: StageFiltersProps
): Promise<StagesResponse> {
  const params = new URLSearchParams();

  if (filters?.page) params.append('page', filters.page);
  if (filters?.per_page) params.append('per_page', filters.per_page.toString());
  if (filters?.search) params.append('search', filters.search);

  const stages = await httpRequest.get<StagesResponse>(
    `/clinic/crm/stages${params.toString() ? `?${params.toString()}` : ''}`
  );
  return stages.data;
}

// Hook para buscar estágios paginados
export function useFetchStagesQuery(filters?: StageFiltersProps) {
  const stagesQuery = useQuery({
    queryKey: ["stages", filters],
    queryFn: () => fetchStages(filters),
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  return {
    ...stagesQuery,
    data: stagesQuery?.data,
  };
}

// Função para buscar estágios sem paginação (para selects e kanban)
async function fetchStagesNoPaginate(): Promise<AxiosResponse<Stage[]>> {
  return await httpRequest.get<Stage[]>(`/clinic/crm/stages`);
}

export function useFetchStagesNoPaginateQuery() {
  const stagesQuery = useQuery({
    queryKey: ["stages", "list"],
    queryFn: () => fetchStagesNoPaginate(),
    keepPreviousData: true,
    staleTime: 10 * 60 * 1000, // 10 minutos
  });

  return {
    ...stagesQuery,
    data: stagesQuery?.data?.data,
  };
}

// Função para buscar estágio por ID
async function fetchStageById(id: string): Promise<AxiosResponse<Stage>> {
  return await httpRequest.get<Stage>(`/clinic/crm/stages/${id}`);
}

export function useFetchStageByIdQuery(id: string) {
  const stageQuery = useQuery({
    queryKey: ["stages", id],
    queryFn: () => fetchStageById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  return {
    ...stageQuery,
    data: stageQuery?.data?.data,
  };
}